import { MenuItem } from '@/types';
import { router } from '@inertiajs/react';
import { ChevronDown, ChevronLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import Loader from './loader';
import { ShareMenu } from './share-menu';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

export const MenuItemBody = ({
    response,
}: {
    response: { menuItem: MenuItem };
}) => {
    if (typeof window === 'undefined') {
        return <Loader />;
    }

    const containerRef = useRef<HTMLDivElement>(null);
    const imageRef = useRef<HTMLDivElement>(null);
    const [scrollProgress, setScrollProgress] = useState(0);

    useEffect(() => {
        const handleScroll = () => {
            if (!containerRef.current || !imageRef.current) return;

            const imageHeight = imageRef.current.offsetHeight;
            const scrolled = window.scrollY;
            const progress = Math.min(scrolled / (imageHeight * 0.6), 1);

            setScrollProgress(progress);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    if (!response.menuItem) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
                <p className="text-muted-foreground">Loading item...</p>
            </div>
        );
    }

    const item = response.menuItem;

    return (
        <div ref={containerRef} className="w-full scroll-smooth">
            {/* Hero Image Section with Title Overlay */}
            <div
                ref={imageRef}
                className="relative w-full overflow-hidden bg-linear-to-b from-accent/20 to-background"
                style={{
                    height: `calc(100svh - ${scrollProgress * 40}svh)`,
                    opacity: 1 - scrollProgress * 0.3,
                }}
            >
                {/* Background Image */}
                <img
                    src={item.image}
                    alt={item.title}
                    loading="eager"
                    className="h-full w-full object-cover"
                    style={{
                        transform: `scale(${1 + scrollProgress * 0.2})`,
                        transitionDuration: '0ms',
                    }}
                />

                {/* Dark Overlay for Text Readability */}
                <div
                    className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-transparent"
                    style={{ opacity: 0.7 + scrollProgress * 0.3 }}
                />

                {/* Title Overlay */}
                <div
                    className="absolute inset-0 flex flex-col items-start justify-end p-6 md:p-8"
                    style={{
                        transform: `translateY(${scrollProgress * 80}px)`,
                        opacity: 1 - scrollProgress * 0.5,
                    }}
                >
                    <h1 className="mb-2 text-4xl font-bold text-white md:text-5xl">
                        {item.title}
                    </h1>
                    <p className="text-lg text-white/80">{item.excerpt}</p>
                    <div className="flex w-full justify-center rounded-md bg-transparent p-2">
                        <ChevronDown className="text-white" size={36} />
                    </div>
                </div>
            </div>

            {/* Sticky Action Bar */}
            <ActionBar item={item} scrollProgress={scrollProgress} />
            {/* Details Section */}
            <div className="relative z-10 bg-background">
                <div className="container max-w-3xl py-12">
                    {/* Price and Rating */}
                    <div className="mb-8 flex items-end justify-between">
                        <div>
                            <div className="mb-3 flex items-baseline gap-2">
                                <span
                                    id="price"
                                    className="scroll-mt-[150px]"
                                />
                                <span className="text-4xl font-bold text-primary">
                                    {item.price} EGP
                                </span>
                            </div>
                            {/* TODO: Add rating */}
                        </div>
                    </div>

                    {/* Description */}
                    <div className="mb-8">
                        <h2 className="mb-4 text-2xl font-semibold text-foreground">
                            Description
                        </h2>
                        <p className="text-lg leading-relaxed text-muted-foreground">
                            {item.description}
                        </p>
                    </div>

                    {/* Category */}
                    <div className="mb-8">
                        <h2 className="mb-4 text-2xl font-semibold text-foreground">
                            Category
                        </h2>
                        <div className="flex flex-wrap gap-6">
                            <Badge
                                variant="secondary"
                                className="px-3 py-2 text-base"
                            >
                                {typeof item.category === 'object'
                                    ? item.category.name
                                    : item.category}
                            </Badge>
                        </div>
                    </div>

                    {/* Ingredients */}
                    <div className="mb-8">
                        <h2 className="mb-4 text-2xl font-semibold text-foreground">
                            Key Ingredients
                        </h2>
                        <div className="flex flex-wrap gap-2 md:gap-4">
                            {item.ingredients.map((ingredient) => (
                                <Badge
                                    key={
                                        typeof ingredient === 'object'
                                            ? ingredient.id
                                            : ingredient
                                    }
                                    variant="secondary"
                                    className="px-3 py-2 text-base"
                                >
                                    {typeof ingredient === 'object'
                                        ? ingredient.name
                                        : ingredient}
                                </Badge>
                            ))}
                        </div>
                    </div>

                    {/* Highlights */}
                    {/* <div className="mb-12">
                        <h2 className="mb-4 text-2xl font-semibold text-foreground">
                            Highlights
                        </h2>
                        <div className="space-y-3">
                            {[
                                {
                                    icon: Clock,
                                    text: 'Prepared fresh to order (15-20 minutes)',
                                },
                                { icon: Flame, text: 'Spice level: Mild' },
                            ].map(({ icon: Icon, text }) => (
                                <div
                                    key={text}
                                    className="flex items-center gap-3"
                                >
                                    <Icon className="h-5 w-5 flex-shrink-0" />
                                    <span className="text-muted-foreground">
                                        {text}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div> */}

                    {/* TODO: Add CTA Section */}
                </div>
            </div>
        </div>
    );
};

function ActionBar({
    item,
    scrollProgress,
}: {
    item: MenuItem;
    scrollProgress: number;
}) {
    const goBack = () => {
        if (window.history.length > 1) {
            window.history.back();
        } else {
            router.visit('/'); // fallback
        }
    };

    const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
    const shareTitle = item.title;
    const shareDescription = item.excerpt || item.description;
    const shareImage = item.image;

    return (
        <div
            className={`sticky top-0 z-40 border-b border-border bg-background transition-all duration-300 ${
                scrollProgress > 0.5 ? 'shadow-lg' : ''
            }`}
            style={{ opacity: scrollProgress * 2 }}
        >
            <div className="mx-auto flex max-w-3xl items-stretch justify-between px-6 py-3">
                <div className="flex items-stretch gap-2">
                    <Button
                        onClick={goBack}
                        className="h-full"
                        size="icon"
                        variant="outline"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h2 className="font-semibold text-foreground">
                            {item.title}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {item.price} EGP
                        </p>
                    </div>
                </div>
                <div className="flex gap-2">
                    {/* TODO: Add favorite button */}
                    <ShareMenu
                        url={shareUrl}
                        title={shareTitle}
                        description={shareDescription}
                        image={shareImage}
                    />
                </div>
            </div>
        </div>
    );
}
